<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { useField, useForm } from 'vee-validate'
import { object, string } from 'yup'
import { useI18n } from 'vue-i18n'
import { DTextField, DBtn, EyeIcon, EyeOffIcon } from 'front-library'

/**
 * store, router, notifications
 */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/**
 * Datos componente
 */
const showNewPassword = ref(true)
const showConfirmNewPassword = ref(true)
const loading = ref(false)
const wrongData = ref('')

/**
 * Datos formulario compañia
 */
const dataChangePassword = ref({
  userCode: '',
  userNewPassword: '',
  userConfirmNewPassword: ''
})

/**
 * Esquema de validacion campos obligatorios
 */
const validationSchema = object().shape({
  userCode: string()
    .matches(/^[0-9]{6}$/g, t('auth.changePassword.form.valid.userCode'))
    .required(t('auth.changePassword.form.valid.required')),
  userNewPassword: string()
    .matches(
      /^(?=(?:.*\d))(?=.*[A-Z])(?=.*[a-z])(?=.*[;:.,_\-*!?¿¡/#$%&+|°"<>'])\S{8,64}$/g,
      t('auth.changePassword.form.valid.password')
    )
    .required(t('auth.changePassword.form.valid.required')),
  userConfirmNewPassword: string().when(
    'userNewPassword',
    (userNewPassword, schema) => {
      if (errors.value.userNewPassword === undefined) {
        return schema
          .equals(
            userNewPassword,
            t('auth.changePassword.form.valid.equalsPassword')
          )
          .required(t('auth.changePassword.form.valid.required'))
      } else {
        return schema
          .matches(
            /^(?=(?:.*\d))(?=.*[A-Z])(?=.*[a-z])(?=.*[;:.,_\-*!?¿¡/#$%&+|°"<>'])\S{8,64}$/g,
            t('auth.changePassword.form.valid.password')
          )
          .required(t('auth.changePassword.form.valid.required'))
      }
    }
  )
})

/**
 * Uso del metodo de validación, retorno de errores y método para reiniciar formulario
 */
const { handleSubmit, errors } = useForm({
  initialValues: dataChangePassword,
  validationSchema
})

/**
 * Definición de campos del formulario
 */
const { value: userCode } = useField<string>('userCode')
const { value: userNewPassword } = useField<string>('userNewPassword')
const { value: userConfirmNewPassword } = useField<string>(
  'userConfirmNewPassword'
)

/*
 * Metodo que valida los campos obligatorios del formulario
 */
const onSubmit = handleSubmit(async (values, actions) => {
  loading.value = true
  const result = await changePassword(values.userCode, values.userNewPassword)
  if (result) {
    wrongData.value = ''
    store.emailForgot = ''
    notifications.addNotification({
      title: t('auth.changePassword.notification.change'),
      text: ``,
      type: 'success',
      time: 10000
    })
    router.push({ name: 'Login' })
  }

  loading.value = false
})

/**
 * Metodo para cambiar la contraseña
 */
const changePassword = async (userCode: any, userNewPassword: any) => {
  try {
    return await store.passwordResetConfirm(
      store.emailForgot,
      userCode,
      userNewPassword
    )
  } catch (error: any) {
    if (error?.code === 'CodeMismatchException') {
      wrongData.value = 'Invalid verification code provided, please try again.' // TODO: Translate
    } else {
      wrongData.value = error.message
    }
  }
}

onMounted(async () => {
  if (store.emailForgot === '') {
    await router.push({ name: 'Login' })
  } else {
    const inputsPassword = document.querySelectorAll('.block-input')
    for (let x = 0; x < inputsPassword.length; x++) {
      ;(inputsPassword[x] as any).onpaste = function (e: any) {
        e.preventDefault()
        notifications.addNotification({
          title: t('auth.changePassword.notification.actionNotAllowed'),
          text: ``,
          type: 'error',
          time: 10000
        })
      }
      ;(inputsPassword[x] as any).oncopy = function (e: any) {
        e.preventDefault()
        notifications.addNotification({
          title: t('auth.changePassword.notification.actionNotAllowed'),
          text: ``,
          type: 'error',
          time: 10000
        })
      }
    }
  }
})

// onBeforeUnmount(() => {
//   store.emailForgot = ''
// })
</script>
<template>
  <h2
    class="text-3xl xl:text-4xl font-bold"
    v-html="t('auth.changePassword.title')" />
  <p
    class="py-3 leading-5 text-gray-500"
    v-html="t('auth.changePassword.description')" />
  <p>Email: {{ store.emailForgot }}</p>
  <div
    v-if="wrongData"
    class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mt-4"
    role="alert">
    <p class="text-sm font-bold">{{ wrongData }}</p>
  </div>
  <form class="grid" @submit="onSubmit">
    <div class="grid mb-3">
      <DTextField
        :label="t('auth.changePassword.form.code')"
        type="number"
        :error="errors.userCode"
        :placeholder="t('auth.changePassword.form.placeholder.code')"
        :required="true"
        v-model="userCode" />
    </div>
    <div class="grid relative mb-3">
      <DTextField
        class-input="block-input"
        :label="t('auth.changePassword.form.newPassword')"
        :type="showNewPassword ? 'password' : 'text'"
        :error="errors.userNewPassword"
        :placeholder="t('auth.changePassword.form.placeholder.newPassword')"
        :required="true"
        v-model="userNewPassword" />
      <div class="absolute right-3 top-8 z-50">
        <EyeIcon
          class="text-gray-600"
          @click="showNewPassword = !showNewPassword"
          :class="{
            hidden: !showNewPassword,
            block: showNewPassword
          }" />
        <EyeOffIcon
          class="text-gray-600"
          @click="showNewPassword = !showNewPassword"
          :class="{
            block: !showNewPassword,
            hidden: showNewPassword
          }" />
      </div>
    </div>
    <div class="grid relative mb-3">
      <DTextField
        class-input="block-input"
        :label="t('auth.changePassword.form.confirmNewPassword')"
        :type="showConfirmNewPassword ? 'password' : 'text'"
        :error="errors.userConfirmNewPassword"
        :placeholder="
          t('auth.changePassword.form.placeholder.confirmNewPassword')
        "
        :required="true"
        v-model="userConfirmNewPassword" />
      <div class="absolute right-3 top-8 z-50">
        <EyeIcon
          class="text-gray-600"
          @click="showConfirmNewPassword = !showConfirmNewPassword"
          :class="{
            hidden: !showConfirmNewPassword,
            block: showConfirmNewPassword
          }" />
        <EyeOffIcon
          class="text-gray-600"
          @click="showConfirmNewPassword = !showConfirmNewPassword"
          :class="{
            block: !showConfirmNewPassword,
            hidden: showConfirmNewPassword
          }" />
      </div>
    </div>
    <DBtn
      class="font-semibold py-3 text-base"
      :loading="loading"
      :color="
        errors.userCode == null &&
        errors.userNewPassword == null &&
        errors.userConfirmNewPassword == null &&
        userCode != '' &&
        userNewPassword != '' &&
        userConfirmNewPassword != ''
          ? 'blue'
          : 'disabled'
      "
      :disabled="
        errors.userCode == null &&
        errors.userNewPassword == null &&
        errors.userConfirmNewPassword == null &&
        userCode != '' &&
        userNewPassword != '' &&
        userConfirmNewPassword != ''
          ? false
          : true
      ">
      {{ t('auth.changePassword.title') }}
    </DBtn>
  </form>
  <div class="flex justify-between pt-4 pb-6">
    <p>{{ t('auth.forgotPassword.havePassword') }}</p>
    <a
      @click="() => router.replace({ name: 'Login' })"
      class="text-blue-600 hover:text-blue-500 font-bold cursor-pointer">
      {{ t('auth.forgotPassword.logIn') }}
    </a>
  </div>
</template>
